// CoLOS Legend schema knowledge: child element order, enums, and templates for
// new fields / a blank label. Sources: docs/colos-schema/colos-legend.xsd (XSD),
// the corpus in tests/fixtures/corpus (what CoLOS 6.2 actually writes), docs/FORMAT.md.
// Pure module: templates are XML strings parsed by our own parser — no DOM.

import { parseXml, child, at, setText } from './xml.js';

// ------------------------------------------------------------------ element sets

/** Every render-style element name allowed directly in an AlphaNumeric (XSD RenderStyleChoice + EAN128/Composite choices). */
export const ALL_RENDER_STYLES = [
  'Text', 'Hidden', 'EAN13', 'Code39', 'EAN8', 'UPCA', 'UPCE', 'ITF', 'Code128', 'DataMatrix', 'PDF417', 'QR', 'RSS',
  'Code128Ean', 'RSSExpanded', 'RSSExpandedStacked',
  'CompositeRenderStyle', 'EANCompositeRenderStyle', 'RSSExpandedStackedComposite',
];

/** Every data-source element name allowed directly in an AlphaNumeric (XSD DataSourceChoice + EAN128/Composite choices). */
export const ALL_DATA_SOURCES = [
  'Static', 'Calculated', 'TimeDate', 'UserInput', 'MachineSetting', 'Merged', 'UserPromptedDate',
  'CalendarMaintainedDate', 'LinkedDate', 'Math', 'AppIDDataList', 'Composite', 'EANComposite',
];

// ------------------------------------------------------------------ child order

const RENDER_BASE = ['Inverted', 'OverlayMode', 'AppInfo'];
const ONE_D = [...RENDER_BASE, 'BarHeight', 'QuietMargin', 'NarrowBarWidth', 'AutoCheckDigit', 'TextDisplayOptions'];
const TWO_D = [...RENDER_BASE, 'QuietMargin', 'CellWidth', 'ErrorCorrection'];

/** Child element order per parent element name (used by xml.insertOrdered). */
export const ORDER = {
  Legend: ['DeviceInfo', 'Description', 'Mirrored', 'Orientation', 'Width', 'Height', 'FieldList', 'ImageAreas', 'AppInfo'],
  DeviceInfo: ['DeviceName'],
  AlphaNumeric: ['Name', 'ID', 'Geometry', 'FieldColor', 'AppInfo', 'Logged', ...ALL_RENDER_STYLES, ...ALL_DATA_SOURCES],
  Geometry: ['X', 'Y', 'Rotation'],
  // render styles
  Text: [...RENDER_BASE, 'Font', 'LegacyFont', 'Paragraph', 'FitToBox'],
  Hidden: [...RENDER_BASE, 'Height', 'Width'],
  EAN13: ONE_D,
  EAN8: ONE_D,
  UPCA: ONE_D,
  Code39: ONE_D,
  UPCE: [...ONE_D, 'ZeroSuppression'],
  ITF: [...ONE_D, 'BorderStyle'],
  Code128: [...ONE_D, 'CodeSet'],
  Code128Ean: [...ONE_D, 'CodeSet'],
  DataMatrix: TWO_D,
  QR: TWO_D,
  // CoLOS 6.2 writer order (corpus); the XSD lists RightToLeftReadingOrder first.
  Font: ['Typeface', 'RightToLeftReadingOrder', 'CharHeight', 'CharWidth', 'PointSizeInHM', 'Filename', 'Bold', 'Italic', 'Underline', 'CharSpacing'],
  // data sources
  Static: ['AppInfo', 'Text'],
  // IsFixed is not in the XSD but CoLOS 6.2 writes it after OffsetRule.
  TimeDate: ['AppInfo', 'TDFormatString', 'LanguageValue', 'OffsetRule', 'IsFixed'],
  OffsetRule: ['OffsetDays', 'OffsetMonths', 'OffsetYears', 'OffsetHours', 'PromptingBehaviour', 'RangeLimits', 'AppInfo'],
};

// ------------------------------------------------------------------ enums (XSD)

export const INK_COLORS = ['BLACK', 'BLUE', 'GREEN', 'RED', 'WHITE']; // EnumInkColors
export const ORIENTATIONS = ['DEG_0', 'DEG_90', 'DEG_180', 'DEG_270']; // EnumRotation (Legend/Orientation)
export const ROTATIONS = ORIENTATIONS;
export const FIELD_ROTATIONS = [0, 90, 180, 270]; // Geometry/Rotation is an integer in degrees
export const OVERLAY_MODES = ['MERGE', 'BLOCK']; // EnumOverlay
export const HUMAN_READABLE = ['DISPLAY_DATA', 'DISPLAY_WITH_CHECKDIGIT', 'DO_NOT_DISPLAY']; // EnumHumanReadable
export const CODE128_CODE_SETS = ['Auto', 'CodeA', 'CodeB', 'CodeC']; // EnumCode128CodeSet
export const DATAMATRIX_ECC = ['ECC_NONE', 'ECC_50', 'ECC_80', 'ECC_100', 'ECC_140', 'ECC_200']; // EnumDataMatrixEcc
export const QR_ECC = ['QREC_0', 'QREC_1', 'QREC_2', 'QREC_3']; // EnumQREcc
export const ITF_BORDER_STYLES = ['ABUTTED_BOX', 'NONE', 'HORIZONTAL_RULES']; // EnumITFBorderStyle
export const OFFSET_PROMPTING = ['PROMPT_YY_MM_DD', 'DO_NOT_PROMPT']; // EnumOffsetPrompting
export const LANGUAGES = [ // EnumLanguageChoice (spelling "BRAZILLIAN" is the vendor's)
  'ENGLISH', 'FRENCH', 'GERMAN', 'SPANISH', 'ITALIAN', 'DUTCH', 'JAPANESE', 'MANDARIN', 'HEBREW', 'ARABIC',
  'TURKISH', 'RUSSIAN', 'POLISH', 'GREEK', 'BRAZILLIAN_PORTUGUESE', 'FINNISH', 'NORWEGIAN', 'DANISH', 'SWEDISH',
];

export const ENUMS = {
  INK_COLORS, ORIENTATIONS, FIELD_ROTATIONS, OVERLAY_MODES, HUMAN_READABLE, CODE128_CODE_SETS,
  DATAMATRIX_ECC, QR_ECC, ITF_BORDER_STYLES, OFFSET_PROMPTING, LANGUAGES,
};

// ------------------------------------------------------------------ render styles / data sources

/** Render styles the editor supports, by field kind (element names). */
export const RENDER_STYLES = {
  text: ['Text'],
  // Only these six are editable. Any other barcode render style (UPCA, UPCE, Code39, ITF, PDF417, RSS…)
  // found in a file is classified 'unsupported': read-only and preserved byte-for-byte.
  barcode: ['EAN13', 'EAN8', 'Code128', 'Code128Ean', 'DataMatrix', 'QR'],
};

/**
 * Per render style: `type` attribute value, family, and whether the type value is verified
 * against a file written by CoLOS. Only `Text` is in the corpus. Barcode type values follow
 * the `<Name>.V1` convention; the SmartDate driver registers QR as "GS1QR". Golden files from
 * CoLOS Designer will confirm/replace these (verified:false until then).
 */
export const RENDER_STYLE_INFO = {
  Text: { type: 'TextBased.V1', family: 'text', verified: true },
  EAN13: { type: 'EAN13.V1', family: 'oneD', verified: false },
  EAN8: { type: 'EAN8.V1', family: 'oneD', verified: false },
  Code128: { type: 'Code128.V1', family: 'oneD', verified: false },
  Code128Ean: { type: 'Code128Ean.V1', family: 'oneD', verified: false },
  DataMatrix: { type: 'DataMatrix.V1', family: 'twoD', verified: false },
  QR: { type: 'GS1QR.V1', family: 'twoD', verified: false },
};

/** Data sources the editor supports, by field kind (element names). */
export const DATA_SOURCES = {
  text: ['Static'],
  date: ['TimeDate'],
  barcode: ['Static'],
};

export const DATA_SOURCE_INFO = {
  Static: { type: 'StaticSrc.V1', verified: true },
  TimeDate: { type: 'TimeDateSrc.V1', verified: true },
};

/** CoLOS-style name prefixes for new fields (TEXT, TEXT_2, …). */
export const NAME_PREFIX = { text: 'TEXT', date: 'DATE', barcode: 'BARCODE' };

// ------------------------------------------------------------------ devices

export const DEVICES = {
  SDX65iw: { key: 'SDX65iw', uri: 'NGEDriver[SDX65iw]/', name: 'SmartDate X65 128mm Intermittent', width: 12800, height: 7100 },
  SDX60cw: { key: 'SDX60cw', uri: 'NGEDriver[SDX60cw]/', name: 'SmartDate X60 128mm Continuous', width: 12800, height: 7100 },
};

export const HEADER_COMMENT = 'Written by CoLOS Designer V6.2.0.0 Freeware';

// ------------------------------------------------------------------ XML fragments (copied from corpus)

const FIELD_APPINFO =
  '<AppInfo><PropertyBag type="PropertyBag.V1"><Item><Key>PerJobData</Key><Value type="PerJobData.V1"><PropertyMap /></Value></Item>' +
  '<Item><Key>IsHiddenAppInfo</Key><Value type="IsHiddenAppInfo.V1"><IsHidden>false</IsHidden></Value></Item></PropertyBag></AppInfo>';
const RENDER_APPINFO = '<AppInfo><PropertyBag type="PropertyBag.V1" /></AppInfo>';
const SOURCE_APPINFO =
  '<AppInfo><PropertyBag type="PropertyBag.V1"><Item><Key>DataSource.PerJobData</Key><Value type="PerJobData.V1"><PropertyMap /></Value></Item></PropertyBag></AppInfo>';
const LEGEND_APPINFO =
  '<AppInfo><PropertyBag type="ImagePropertyBag.V1"><RemotingMap type="RemotingInfoList.V1"><RemotingLinks /></RemotingMap>' +
  '<Freeware type="Freeware.URI" /><Rotation type="RotationImageAppInfo.URI" />' +
  '<RibbonUsage type="RibbonUsageAppInfo"><RibbonWidth>13000</RibbonWidth><RibbonLength>90000000</RibbonLength></RibbonUsage>' +
  '<RepeatQueueAppInfo type="RepeatQueueAppInfo"><IsEnableQueueChecked>false</IsEnableQueueChecked><GridRowValues><Entry>0</Entry></GridRowValues></RepeatQueueAppInfo>' +
  '<BackgroundImageAppInfo type="BackgroundImageAppInfo"><BackgroundImageRect /><BackgroundImageRotation>0</BackgroundImageRotation><BackgroundImage /></BackgroundImageAppInfo>' +
  '<PerJobData type="PerJobData.V1"><PropertyMap /></PerJobData></PropertyBag></AppInfo>';

const FIELD_HEAD =
  '<AlphaNumeric type="AlphaNum.V1"><Name>TEXT</Name><ID>0</ID><Geometry><X>1000</X><Y>1000</Y><Rotation>0</Rotation></Geometry>' +
  '<FieldColor>BLACK</FieldColor>' + FIELD_APPINFO + '<Logged>false</Logged>';

const TEXT_RENDER_STYLE =
  '<Text type="TextBased.V1"><Inverted>false</Inverted><OverlayMode>MERGE</OverlayMode>' + RENDER_APPINFO +
  '<Font type="Font.V1"><Typeface>Arial</Typeface><RightToLeftReadingOrder>false</RightToLeftReadingOrder>' +
  '<CharHeight>487</CharHeight><CharWidth>423</CharWidth><Filename /><Bold>false</Bold><Italic>false</Italic><Underline>false</Underline></Font></Text>';

const STATIC_SOURCE = '<Static type="StaticSrc.V1">' + SOURCE_APPINFO + '<Text>Text</Text></Static>';

const TIMEDATE_SOURCE =
  '<TimeDate type="TimeDateSrc.V1">' + SOURCE_APPINFO +
  '<TDFormatString>%D.%M.%Y</TDFormatString><LanguageValue>ENGLISH</LanguageValue>' +
  '<OffsetRule><OffsetDays>0</OffsetDays><OffsetMonths>0</OffsetMonths><OffsetYears>0</OffsetYears><OffsetHours>0</OffsetHours>' +
  '<PromptingBehaviour>DO_NOT_PROMPT</PromptingBehaviour></OffsetRule><IsFixed>false</IsFixed></TimeDate>';

/** Default text font for new fields (most common size in the corpus). */
export const DEFAULT_FONT = { typeface: 'Arial', height: 487, width: 423, bold: false, italic: false, underline: false, rtl: false };

// ------------------------------------------------------------------ barcode defaults

// Set to true once golden files written by CoLOS Designer confirm element types, child order and
// default values for barcode fields. Until then these are derived from the XSD sequence only.
export const BARCODE_TEMPLATE_VERIFIED = false;

export const BARCODE_DEFAULTS = {
  EAN13: { barHeight: 1000, quietMargin: true, narrowBarWidth: 33, autoCheckDigit: true, textDisplay: 'DISPLAY_WITH_CHECKDIGIT', data: '590123412345' },
  EAN8: { barHeight: 1000, quietMargin: true, narrowBarWidth: 33, autoCheckDigit: true, textDisplay: 'DISPLAY_WITH_CHECKDIGIT', data: '9638507' },
  Code128: { barHeight: 1000, quietMargin: true, narrowBarWidth: 33, autoCheckDigit: true, textDisplay: 'DISPLAY_DATA', codeSet: 'Auto', data: 'ABC123' },
  Code128Ean: { barHeight: 1000, quietMargin: true, narrowBarWidth: 33, autoCheckDigit: true, textDisplay: 'DISPLAY_DATA', codeSet: 'Auto', data: '0109501101530003' },
  DataMatrix: { quietMargin: true, cellWidth: 50, errorCorrection: 'ECC_200', data: 'BAGASON' },
  QR: { quietMargin: true, cellWidth: 50, errorCorrection: 'QREC_1', data: 'BAGASON' },
};

// ------------------------------------------------------------------ builders

function fragment(xml) {
  return parseXml(xml).children[0];
}

const b = (v) => (v ? 'true' : 'false');

// Integers are written verbatim — never rounded, never in exponent notation.
function intStr(v) {
  if (!Number.isSafeInteger(v)) throw new Error(`Template value must be a whole number, got ${String(v)}`);
  return String(v);
}

function applyFont(fontEl, font) {
  const f = { ...DEFAULT_FONT, ...(font || {}) };
  setText(child(fontEl, 'Typeface'), f.typeface);
  setText(child(fontEl, 'RightToLeftReadingOrder'), b(f.rtl));
  setText(child(fontEl, 'CharHeight'), intStr(f.height));
  setText(child(fontEl, 'CharWidth'), intStr(f.width));
  setText(child(fontEl, 'Bold'), b(f.bold));
  setText(child(fontEl, 'Italic'), b(f.italic));
  setText(child(fontEl, 'Underline'), b(f.underline));
}

function applyHead(el, { name, id = 0, x = 1000, y = 1000 }) {
  setText(child(el, 'Name'), name);
  setText(child(el, 'ID'), intStr(id));
  setText(at(el, ['Geometry', 'X']), intStr(x));
  setText(at(el, ['Geometry', 'Y']), intStr(y));
}

/** New static Text field, structurally identical to corpus text fields. */
export function textFieldTemplate({ name = 'TEXT', id = 0, x = 1000, y = 1000, text = 'Text', font } = {}) {
  const el = fragment(FIELD_HEAD + TEXT_RENDER_STYLE + STATIC_SOURCE + '</AlphaNumeric>');
  applyHead(el, { name, id, x, y });
  applyFont(at(el, ['Text', 'Font']), font);
  setText(at(el, ['Static', 'Text']), text);
  return el;
}

/** New Static data-source element, structurally identical to the Static sources in real labels. */
export function staticSourceTemplate(text = 'Text') {
  const el = fragment(STATIC_SOURCE);
  setText(child(el, 'Text'), text);
  return el;
}

/** New TimeDate field, structurally identical to corpus date fields. */
export function dateFieldTemplate({
  name = 'DATE', id = 0, x = 1000, y = 1000, font, format = '%D.%M.%Y', language = 'ENGLISH', offset = {},
} = {}) {
  const el = fragment(FIELD_HEAD + TEXT_RENDER_STYLE + TIMEDATE_SOURCE + '</AlphaNumeric>');
  applyHead(el, { name, id, x, y });
  applyFont(at(el, ['Text', 'Font']), font);
  const td = child(el, 'TimeDate');
  setText(child(td, 'TDFormatString'), format);
  setText(child(td, 'LanguageValue'), language);
  const rule = child(td, 'OffsetRule');
  setText(child(rule, 'OffsetDays'), intStr(offset.days || 0));
  setText(child(rule, 'OffsetMonths'), intStr(offset.months || 0));
  setText(child(rule, 'OffsetYears'), intStr(offset.years || 0));
  setText(child(rule, 'OffsetHours'), intStr(offset.hours || 0));
  return el;
}

/** Barcode render-style element per XSD sequence (unverified against CoLOS output — see BARCODE_TEMPLATE_VERIFIED). */
export function barcodeRenderStyleTemplate(symbology = 'EAN13') {
  const info = RENDER_STYLE_INFO[symbology];
  if (!info || info.family === 'text' || !RENDER_STYLES.barcode.includes(symbology)) {
    throw new Error(`Unsupported barcode symbology: ${symbology}`);
  }
  const d = BARCODE_DEFAULTS[symbology];
  let inner = '<Inverted>false</Inverted><OverlayMode>MERGE</OverlayMode>' + RENDER_APPINFO;
  if (info.family === 'oneD') {
    inner +=
      `<BarHeight>${d.barHeight}</BarHeight><QuietMargin>${b(d.quietMargin)}</QuietMargin>` +
      `<NarrowBarWidth>${d.narrowBarWidth}</NarrowBarWidth><AutoCheckDigit>${b(d.autoCheckDigit)}</AutoCheckDigit>` +
      `<TextDisplayOptions>${d.textDisplay}</TextDisplayOptions>`;
    if (symbology === 'Code128' || symbology === 'Code128Ean') inner += `<CodeSet>${d.codeSet}</CodeSet>`;
  } else {
    inner +=
      `<QuietMargin>${b(d.quietMargin)}</QuietMargin><CellWidth>${d.cellWidth}</CellWidth>` +
      `<ErrorCorrection>${d.errorCorrection}</ErrorCorrection>`;
  }
  return fragment(`<${symbology} type="${info.type}">${inner}</${symbology}>`);
}

/** New barcode field: AlphaNumeric + barcode render style + Static source. */
export function barcodeFieldTemplate({ name = 'BARCODE', id = 0, x = 1000, y = 1000, symbology = 'EAN13', data } = {}) {
  const el = fragment(FIELD_HEAD + STATIC_SOURCE + '</AlphaNumeric>');
  applyHead(el, { name, id, x, y });
  const src = child(el, 'Static');
  el.children.splice(el.children.indexOf(src), 0, barcodeRenderStyleTemplate(symbology));
  setText(child(src, 'Text'), data === undefined ? BARCODE_DEFAULTS[symbology].data : data);
  return el;
}

/** Blank Legend document (header comment + Legend with empty FieldList), from the corpus X65 skeleton. */
export function blankLegendTemplate(deviceKey = 'SDX65iw') {
  const dev = DEVICES[deviceKey];
  if (!dev) throw new Error(`Unknown device: ${deviceKey}`);
  const doc = parseXml(
    `<!--${HEADER_COMMENT}-->` +
      '<Legend type="Image.V1" PaperColor="White" BackgroundColor="DarkGray">' +
      '<DeviceInfo DeviceURI="NGEDriver[SDX65iw]/"><DeviceName>SmartDate X65 128mm Intermittent</DeviceName></DeviceInfo>' +
      '<Description /><Mirrored>false</Mirrored><Orientation>DEG_0</Orientation>' +
      '<Width>12800</Width><Height>7100</Height><FieldList />' + LEGEND_APPINFO + '</Legend>',
  );
  const legend = doc.children.find((c) => c.type === 'element');
  const info = child(legend, 'DeviceInfo');
  info.attrs.find((a) => a.name === 'DeviceURI').value = dev.uri;
  setText(child(info, 'DeviceName'), dev.name);
  setText(child(legend, 'Width'), dev.width);
  setText(child(legend, 'Height'), dev.height);
  return doc;
}
